import { FormEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import { Button } from '../Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogPanel, DialogTitle } from '../Dialog';
import { Input } from '../Input';
import { TextField } from '../TextField';
export const PromptDialog = (props: PromptDialogProps) => {
  const {
    maxWidth = '320px',
    topSlot,
    title,
    content,
    afterContent,
    dimmed = true,
    input,
    confirmVariant = 'primary',
    cancelText,
    confirmText,
    useOutSideClick = false,
    keyboard = false,
    focus,
    onClose,
    onConfirm,
    ...rest
  } = props;
  const formId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    t
  } = useTranslation(['common']);
  const handleCloseDialog = () => {
    if (!useOutSideClick) {
      return;
    }
    onClose && onClose();
  };
  const handleCancel = () => {
    onClose && onClose();
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onConfirm && onConfirm(e, e.currentTarget[`prompt-dialog-${formId}`].value);
  };
  useEffect(() => {
    const formEl = document.getElementById(`prompt-dialog-${formId}`) as HTMLFormElement;
    const inputEl = formEl?.[`prompt-dialog-${formId}`];
    if (inputEl && focus) {
      inputEl.focus();
    }
  }, [focus, formId]);
  return <Dialog open maxWidth={maxWidth} initialFocus={inputRef} onClose={handleCloseDialog} onKeyDown={e => !keyboard && e.stopPropagation()} {...rest} data-sentry-element="Dialog" data-sentry-component="PromptDialog" data-sentry-source-file="PromptDialog.tsx">
      {dimmed && <DialogOverlay />}
      <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="PromptDialog.tsx">
        <DialogHeader topSlot={topSlot} data-sentry-element="DialogHeader" data-sentry-source-file="PromptDialog.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="PromptDialog.tsx">{title}</DialogTitle>
        </DialogHeader>
        <DialogContent preLine data-sentry-element="DialogContent" data-sentry-source-file="PromptDialog.tsx">
          <When condition={!afterContent && !!content} data-sentry-element="When" data-sentry-source-file="PromptDialog.tsx">
            <div>{content}</div>
          </When>
          <form id={`prompt-dialog-${formId}`} onSubmit={handleSubmit}>
            <TextField className="mt-2" helperText={input?.helperText} error={input?.error} data-sentry-element="TextField" data-sentry-source-file="PromptDialog.tsx">
              <Input ref={inputRef} name={`prompt-dialog-${formId}`} type={input?.type || 'text'} rightSlot={input?.rightSlot} placeholder={input?.placeholder} error={input?.error} defaultValue={input?.defaultValue} autoComplete="off" isFullWidth data-sentry-element="Input" data-sentry-source-file="PromptDialog.tsx" />
            </TextField>
          </form>
          <When condition={afterContent && !!content} data-sentry-element="When" data-sentry-source-file="PromptDialog.tsx">
            <div>{content}</div>
          </When>
        </DialogContent>
        <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="PromptDialog.tsx">
          <When condition={cancelText !== false} data-sentry-element="When" data-sentry-source-file="PromptDialog.tsx">
            <Button variant="sub" size="md" onClick={handleCancel} data-sentry-element="Button" data-sentry-source-file="PromptDialog.tsx">
              {cancelText ?? t('common:cancel')}
            </Button>
          </When>
          <Button type="submit" form={`prompt-dialog-${formId}`} variant={confirmVariant} size="md" data-sentry-element="Button" data-sentry-source-file="PromptDialog.tsx">
            {confirmText ?? t('common:confirm')}
          </Button>
        </DialogFooter>
      </DialogPanel>
    </Dialog>;
};
export type PromptDialogProps = {
  maxWidth?: string;
  topSlot?: ReactNode;
  title: ReactNode;
  content?: ReactNode;
  useOutSideClick?: boolean;

  // Prompt Dialog 특성상 Input 컴포넌트가 기본적으로 포함되게 됩니다.
  // Input 컴포넌트 다음으로 콘텐츠를 노출 하고 싶은 경우 afterContent 를 사용하면 됩니다.
  afterContent?: boolean;
  dimmed?: boolean;
  input?: {
    type?: 'text' | 'password';
    helperText?: string;
    error?: boolean;
    placeholder?: string;
    rightSlot?: ReactNode;
    defaultValue?: string;
  };
  keyboard?: boolean;

  // 버튼 필드
  cancelText?: string | boolean;
  confirmText?: string;
  confirmVariant?: 'primary' | 'alert';
  onClose?: () => void;
  onConfirm?: (event: FormEvent<HTMLFormElement>, value: string) => void;
  focus?: boolean;
};